import React, { useState, useEffect, useRef } from "react";

import * as m from "@/shared/styles/main.module.scss";
import collapse from "@/assets/images/collapse.svg";
import { LeftBorder } from "@/shared/components/LeftBorder";

import RadioGroup from "./RadioGroup";
import { Question } from "../types/valuebuilder";

interface PollQuestionProps {
  question: Question;
  index: number;
  setAnswer: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  child?: boolean;
  childIndex?: number;
  companyName?: string;
}

const PollQuestion: React.FC<PollQuestionProps> = ({
  question,
  index,
  setAnswer,
  child,
  childIndex,
  companyName,
}) => {
  const [isMobile, setScreenSize] = useState<boolean>(false);
  const [isCollapse, setCollapse] = useState(false);
  const [listHeight, setListHeight] = useState<number | undefined>();
  const [attrDisplay, setDisplay] = useState<"grid" | "none" | undefined>();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setDisplay("grid");
      setTimeout(() => {
        if (ref.current) {
          setListHeight(isCollapse ? 0 : ref.current.scrollHeight);
        }
      }, 1);
    }
  }, [isCollapse, isMobile]);

  useEffect(() => {
    if (listHeight === 0) {
      setTimeout(() => {
        setDisplay("none");
      }, 1000);
    }
  }, [listHeight]);

  const changeListHeight = (height: number, increase: boolean) => {
    if (!isCollapse) {
      if (increase) {
        setListHeight(listHeight ?? 0 + height);
      } else {
        setListHeight(listHeight ?? 0 - height);
      }
    }
  };

  const setScreen = () => {
    setScreenSize(window.innerWidth <= 767);
  };
  useEffect(() => {
    setScreen();
    window.addEventListener("resize", setScreen);

    return () => window.removeEventListener("resize", setScreen);
  }, []);

  switch (question.type) {
    case "radio": {
      return (
        <div className={`${m.formGroup} ${child ? m.subGroup : ""}`}>
          <div
            className={`${childIndex ? m.childIndex : m.index} ${
              question.error ? m.error : ""
            }`}
          >
            <LeftBorder />
            <span>{`${index}.${childIndex ? `${childIndex}.` : ""}`}</span>
          </div>
          <label className={m.title}>
            {question.label.replace(
              new RegExp("{{company_name}}", "g"),
              companyName ?? ""
            )}
          </label>
          <label
            className={`${m.requiredLabel} ${question.error ? m.error : ""}`}
          >{`${
            question.is_required
              ? "Question is required."
              : "Question is optional."
          }`}</label>
          <div className={m.answers}>
            {Array.apply(
              null,
              Array(isMobile ? 6 : question.max_value + 1)
            ).map((item, i) => {
              return (
                <div key={i} className={m.radioItem}>
                  <div>
                    <input
                      type="radio"
                      value={i}
                      checked={
                        parseInt(
                          typeof question.value === "string"
                            ? question.value
                            : ""
                        ) === i
                      }
                      id={`question_${question.question_id}_${i}`}
                      data-question-id={question.question_id}
                      onChange={setAnswer}
                    />
                    <label
                      className={m.mark}
                      htmlFor={`question_${question.question_id}_${i}`}
                    >
                      {i}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          {isMobile ? (
            <div className={`${m.answers} ${m.answersMobile}`}>
              {Array.apply(null, Array(5)).map((item, i) => {
                i += 6;
                return (
                  <div key={i} className={m.radioItem}>
                    <div>
                      <input
                        type="radio"
                        value={i}
                        checked={
                          parseInt(
                            typeof question.value === "string"
                              ? question.value
                              : ""
                          ) === i
                        }
                        id={`question_${question.question_id}_${i}`}
                        data-question-id={question.question_id}
                        onChange={setAnswer}
                      />
                      <label
                        className={m.mark}
                        htmlFor={`question_${question.question_id}_${i}`}
                      >
                        {i}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className={m.formLegend}>
            <label>Legend</label>
            <table>
              <tbody>
                <tr>
                  <td>
                    <span>{question.minValue || 0}</span>
                  </td>
                  <td>
                    <span>—</span>
                  </td>
                  <td>
                    <span>{question.label_min}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>{question.max_value}</span>
                  </td>
                  <td>
                    <span>—</span>
                  </td>
                  <td>
                    <span>{question.label_max}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {question.children?.length
            ? question.children.map((item, childIndex) => (
                <PollQuestion
                  key={item.question_id}
                  question={item}
                  index={index}
                  setAnswer={setAnswer}
                  child={true}
                  childIndex={childIndex + 1}
                  companyName={companyName}
                />
              ))
            : null}
        </div>
      );
    }
    case "textarea": {
      return (
        <div
          className={`${m.formGroup} ${child ? m.subGroup : ""} ${
            m.textQuestion
          } `}
        >
          <div
            className={`${childIndex ? m.childIndex : m.index} ${
              question.error ? m.error : ""
            }`}
          >
            {childIndex ? null : <LeftBorder />}
            <span>{`${index}.${childIndex ? `${childIndex}.` : ""}`}</span>
          </div>
          <label className={m.title}>{question.label}</label>
          <label
            className={`${m.requiredLabel} ${question.error ? m.error : ""}`}
          >{`${
            question.is_required
              ? "Question is required."
              : "Question is optional."
          }`}</label>
          <textarea
            value={typeof question.value === "string" ? question.value : ""}
            name={`question_${question.question_id}`}
            data-question-id={question.question_id}
            onChange={setAnswer}
            placeholder="Please, provide us a few lines"
          />
          {question.children?.length
            ? question.children.map((item, childIndex) => (
                <PollQuestion
                  key={item.question_id}
                  question={item}
                  index={index + 1}
                  setAnswer={setAnswer}
                  child={true}
                  childIndex={childIndex + 1}
                />
              ))
            : null}
        </div>
      );
    }
    case "radiogroup": {
      const questionsGroup = question.questionsGroup;
      const questionsGroupOptions = question.questionsGroupOptions;
      return (
        <div
          className={`${m.formGroup} ${child ? m.subGroup : ""} ${
            m.radioGroup
          }`}
        >
          <div className={childIndex ? m.childIndex : m.index}>
            {childIndex ? null : <LeftBorder />}
            <span>{`${index}.${childIndex ? `${childIndex}.` : ""}`}</span>
          </div>
          <label className={m.title}>{question.label}</label>
          <label
            className={`${m.requiredLabel} ${question.error ? m.error : ""}`}
          >{`${
            question.is_required
              ? "All questions in the list are required."
              : "Question is optional."
          }`}</label>
          <div
            className={m.collapseAll}
            onClick={() => setCollapse(!isCollapse)}
          >
            <img src={collapse} alt="collapse" />
            Collapse Section
          </div>
          <div
            ref={ref}
            className={m.radioListGroup}
            style={{
              height: listHeight,
              opacity: isCollapse ? "0" : "1",
              display: attrDisplay,
            }}
          >
            {questionsGroup &&
              Object.keys(questionsGroup).map(
                (groupedQuestionId, answerIndex) => (
                  <RadioGroup
                    key={answerIndex}
                    options={questionsGroupOptions}
                    label={questionsGroup[groupedQuestionId]}
                    groupedQuestionId={groupedQuestionId}
                    groupedQuestionIndex={answerIndex}
                    parentIndex={index}
                    question={question}
                    setAnswer={setAnswer}
                    changeHeight={changeListHeight}
                  />
                )
              )}
          </div>
          {question.children?.length
            ? question.children.map((item, childIndex) => (
                <PollQuestion
                  key={item.question_id}
                  question={item}
                  index={index}
                  setAnswer={setAnswer}
                  child={true}
                  childIndex={childIndex + 1}
                />
              ))
            : null}
        </div>
      );
    }
    default:
      return null;
  }
};

export default PollQuestion;
